import React, { useState } from 'react';
import { ChevronDown, ChevronUp, User, Lock, Bell, Mail, Shield, CheckCircle, Info, AlertCircle, LogOut } from 'lucide-react';

export function AccountGuide() {
  const [expandedSection, setExpandedSection] = useState<string | null>('overview');

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const Section = ({ id, title, children }: { id: string; title: string; children: React.ReactNode }) => (
    <div className="border border-white/10 rounded-xl overflow-hidden mb-4">
      <button
        onClick={() => toggleSection(id)}
        className="w-full flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 transition-colors"
      >
        <h3 className="text-lg text-white font-medium">{title}</h3>
        {expandedSection === id ? (
          <ChevronUp className="w-5 h-5 text-white/70" />
        ) : (
          <ChevronDown className="w-5 h-5 text-white/70" />
        )}
      </button>
      {expandedSection === id && (
        <div className="p-4 bg-white/[0.02] space-y-4">
          {children}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl text-white font-brand font-light italic mb-2">Account Settings</h2>
        <p className="text-white/80">
          Manage your profile, security, notifications, and subscription preferences.
        </p>
      </div>

      <Section id="overview" title="Accessing Settings">
        <p className="text-white/80 mb-4">
          To access your account settings:
        </p>

        <ol className="space-y-3 text-white/70 text-sm">
          <li className="flex items-start gap-3">
            <span className="text-amber-400 font-bold">1.</span>
            <span>Click on your profile icon in the top-right corner of the header</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-amber-400 font-bold">2.</span>
            <span>Select "Settings" from the dropdown menu</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-amber-400 font-bold">3.</span>
            <span>Navigate between tabs: Profile, Security, and Notifications</span>
          </li>
        </ol>

        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mt-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-blue-200 text-sm">
              Your settings are automatically saved when you make changes. No need to click a "Save" button!
            </p>
          </div>
        </div>
      </Section>

      <Section id="profile" title="Profile Settings">
        <p className="text-white/80 mb-4">
          Manage your basic profile information in the Profile tab:
        </p>

        <div className="space-y-4">
          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-start gap-3 mb-3">
              <Mail className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-white font-medium mb-1">Email Address</h4>
                <p className="text-white/70 text-sm">
                  Your email address is displayed but cannot be changed. This is the email you use to log in
                  and where you'll receive notifications.
                </p>
              </div>
            </div>
            <div className="bg-red-500/10 border border-red-500/20 rounded p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-red-200 text-xs">
                  For security reasons, email changes are not currently supported. If you need to change your email,
                  please contact support.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-start gap-3 mb-3">
              <User className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-white font-medium mb-1">Display Name</h4>
                <p className="text-white/70 text-sm mb-3">
                  Your display name appears throughout the app and in community features like challenges.
                  You can update it at any time.
                </p>

                <div className="bg-white/5 rounded p-3">
                  <p className="text-white/90 font-medium text-sm mb-2">To update your display name:</p>
                  <ol className="text-white/60 text-xs space-y-1.5">
                    <li className="flex items-start gap-2">
                      <span className="text-amber-400">1.</span>
                      <span>Type your new name in the Display Name field</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-400">2.</span>
                      <span>Click the "Save Changes" button</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-400">3.</span>
                      <span>You'll see a success message when it's updated</span>
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-white font-medium mb-1">Email Verification</h4>
                <p className="text-white/70 text-sm mb-2">
                  When you first sign up, you'll receive a verification email. Verifying your email:
                </p>
                <ul className="text-white/60 text-sm space-y-1 ml-4">
                  <li>• Enables notifications and reminders</li>
                  <li>• Confirms your account security</li>
                  <li>• Allows password reset functionality</li>
                  <li>• Required for full app features</li>
                </ul>
                <p className="text-white/70 text-sm mt-3">
                  If you didn't receive the email or it expired, you can request a new one from the banner
                  at the top of the app.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section id="security" title="Security Settings">
        <p className="text-white/80 mb-4">
          Keep your account secure by managing your password in the Security tab:
        </p>

        <div className="space-y-4">
          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-start gap-3 mb-3">
              <Lock className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-white font-medium mb-2">Changing Your Password</h4>
                <p className="text-white/70 text-sm mb-3">
                  To update your password for security reasons:
                </p>

                <div className="bg-white/5 rounded p-3 mb-3">
                  <p className="text-white/90 font-medium text-sm mb-2">Steps to change password:</p>
                  <ol className="text-white/60 text-xs space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-amber-400">1.</span>
                      <span>Navigate to the Security tab in Settings</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-400">2.</span>
                      <span>Enter your current password in the "Current Password" field</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-400">3.</span>
                      <span>Enter your new password in the "New Password" field</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-400">4.</span>
                      <span>Re-enter your new password in the "Confirm New Password" field</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-400">5.</span>
                      <span>Click "Update Password"</span>
                    </li>
                  </ol>
                </div>

                <div className="bg-amber-500/10 border border-amber-500/20 rounded p-3">
                  <p className="text-amber-200 text-sm font-medium mb-1">Password Requirements:</p>
                  <ul className="text-amber-200/80 text-xs space-y-1">
                    <li>• Minimum 6 characters (longer is better!)</li>
                    <li>• Must match in both "New" and "Confirm" fields</li>
                    <li>• Different from your current password</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-white font-medium mb-2">Re-authentication</h4>
                <p className="text-white/70 text-sm mb-2">
                  For security, you must enter your current password before changing it. This prevents unauthorized
                  changes if someone gains access to your unlocked device.
                </p>
                <p className="text-white/60 text-xs italic">
                  If you've forgotten your current password, sign out and use the "Forgot Password" link on the
                  login screen to reset it.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-white font-medium mb-2">Password Reset</h4>
                <p className="text-white/70 text-sm mb-2">
                  If you can't remember your password:
                </p>
                <ol className="text-white/60 text-sm space-y-1.5">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400">1.</span>
                    <span>Sign out of your account</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400">2.</span>
                    <span>Click "Forgot Password?" on the login screen</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400">3.</span>
                    <span>Enter your email address</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400">4.</span>
                    <span>Check your email for a password reset link</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-400">5.</span>
                    <span>Follow the link to create a new password</span>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section id="notifications" title="Notification Preferences">
        <p className="text-white/80 mb-4">
          Control which notifications you receive in the Notifications tab:
        </p>

        <div className="space-y-4">
          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-start gap-3 mb-3">
              <Bell className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-white font-medium mb-2">Email Notifications</h4>
                <p className="text-white/70 text-sm mb-3">
                  You can enable or disable email notifications for different types of events:
                </p>

                <div className="space-y-3">
                  <div className="bg-white/5 rounded p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-white font-medium text-sm">Starter Feeding Reminders</p>
                      <div className="w-10 h-6 bg-amber-500/20 rounded-full"></div>
                    </div>
                    <p className="text-white/60 text-xs">
                      Receive emails when your starter needs feeding. Helps you maintain healthy starters
                      and never miss a feeding.
                    </p>
                  </div>

                  <div className="bg-white/5 rounded p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-white font-medium text-sm">Baking Session Milestones</p>
                      <div className="w-10 h-6 bg-amber-500/20 rounded-full"></div>
                    </div>
                    <p className="text-white/60 text-xs">
                      Get notified at key points in your baking timeline: when to knead, shape, and bake.
                      Essential for timing your bakes perfectly.
                    </p>
                  </div>

                  <div className="bg-white/5 rounded p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-white font-medium text-sm">Challenge Updates</p>
                      <div className="w-10 h-6 bg-amber-500/20 rounded-full"></div>
                    </div>
                    <p className="text-white/60 text-xs">
                      Stay informed about challenge deadlines, new challenges, and community activity
                      in challenges you've joined.
                    </p>
                  </div>

                  <div className="bg-white/5 rounded p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-white font-medium text-sm">Tips & Updates</p>
                      <div className="w-10 h-6 bg-amber-500/20 rounded-full"></div>
                    </div>
                    <p className="text-white/60 text-xs">
                      Receive baking tips, new feature announcements, and educational content to improve
                      your sourdough skills.
                    </p>
                  </div>
                </div>

                <p className="text-white/70 text-sm mt-3">
                  Simply toggle each switch on or off to customize your notification preferences.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-white font-medium mb-2">In-App Notifications</h4>
                <p className="text-white/70 text-sm mb-2">
                  In addition to emails, you'll see notifications in the app:
                </p>
                <ul className="text-white/60 text-sm space-y-1.5 ml-4">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Click the bell icon in the header to view all notifications</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>A badge shows the number of unread notifications</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Click on a notification to view details or take action</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Mark notifications as read or dismiss them</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-200 text-sm font-medium mb-1">Email verification required</p>
                <p className="text-amber-200/80 text-xs">
                  You must verify your email address to receive email notifications. Check your inbox for a
                  verification email when you first sign up.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section id="signout" title="Signing Out">
        <div className="bg-white/5 rounded-lg p-4">
          <div className="flex items-start gap-3 mb-3">
            <LogOut className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-white font-medium mb-2">How to Sign Out</h4>
              <p className="text-white/70 text-sm mb-3">
                To log out of your account:
              </p>
              <ol className="text-white/60 text-sm space-y-1.5 mb-3">
                <li className="flex items-start gap-2">
                  <span className="text-amber-400">1.</span>
                  <span>Click on your profile icon in the top-right corner</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-400">2.</span>
                  <span>Select "Settings"</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-400">3.</span>
                  <span>Scroll to the bottom of any settings tab</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-400">4.</span>
                  <span>Click the "Sign Out" button</span>
                </li>
              </ol>
              <p className="text-white/60 text-xs italic">
                You'll be returned to the login screen. Your data is saved and will be there when you log back in.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mt-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-blue-200 text-sm">
                Always sign out when using shared or public devices to keep your account secure!
              </p>
            </div>
          </div>
        </div>
      </Section>

      <Section id="tips" title="Account Security Tips">
        <ul className="space-y-3 text-white/70 text-sm">
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Use a strong password</span>
              Mix letters, numbers, and symbols. Avoid using personal information or common words.
            </div>
          </li>
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Don't share your password</span>
              Keep your login credentials private. Breadlify staff will never ask for your password.
            </div>
          </li>
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Verify your email</span>
              This enables password recovery and ensures you receive important account notifications.
            </div>
          </li>
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Update regularly</span>
              Change your password periodically, especially if you suspect unauthorized access.
            </div>
          </li>
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Sign out on shared devices</span>
              Always log out when using computers or tablets that others have access to.
            </div>
          </li>
        </ul>
      </Section>
    </div>
  );
}
